<template>
  <ViewerLayoutRoot class="video-guest-layout" data-testid="video-guest-layout">
    <GuestTopBar compact-desktop-top-spacing />

    <!-- Split view: Logs mode or desktop Video mode with chapters -->
    <template v-if="splitConfig">
      <SplitViewContainer class="video-guest-layout__split" :full-width="isTheaterModeEnabled" v-bind="splitConfig">
        <template #left>
          <div class="video-guest-layout__video-col">
            <div class="video-guest-layout__video">
              <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
            </div>
            <MetadataSection :show-toolbar="false" />
          </div>
        </template>
        <template #right>
          <div class="video-guest-layout__side-panel">
            <component :is="splitRightPanel" />
          </div>
        </template>
      </SplitViewContainer>
    </template>

    <!-- Stacked: mobile or no chapters -->
    <div v-else class="video-guest-layout__media-row" :class="{ 'video-guest-layout__media-row--single': !hasChapters || isTheaterModeEnabled }">
      <div class="video-guest-layout__video-col">
        <div class="video-guest-layout__video">
          <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
        </div>
        <div v-if="isSmallMobile" class="mt-3 px-4">
          <ItemName />
        </div>
        <div class="px-4 sm:px-0">
          <MetadataSection :show-toolbar="false" />
        </div>
      </div>
      <div v-if="hasChapters && !isTheaterModeEnabled" class="video-guest-layout__side-panel px-4 sm:px-0">
        <GuestChaptersSidePanel />
      </div>
    </div>

    <EngagementBar />

    <div class="video-guest-layout__tabs px-4 sm:px-0">
      <VideoGuestTabs :show-theater-chapters="isTheaterModeEnabled && hasChapters" @addedComment="emit('addedComment')" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch, watchEffect, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import GuestTopBar from '../../shared/GuestTopBar.vue';
import EngagementBar from '../../shared/EngagementBar.vue';
import MetadataSection from '../../shared/MetadataSection.vue';
import GuestChaptersSidePanel from './GuestChaptersSidePanel.vue';
import VideoGuestTabs from './VideoGuestTabs.vue';
import LogsSidePanel from '@/modules/viewer/components/logs/LogsSidePanel.vue';
import SplitViewContainer from '@/modules/viewer/components/SplitViewContainer.vue';

import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerTheaterMode } from '@/modules/viewer/composables/useViewerTheaterMode';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useViewerModeUrlSync } from '@/modules/viewer/composables/useViewerModeUrlSync';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useMobileDetection } from '@/composables/useMobileDetection';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';
import { VIDEO_RIGHT_PANE_SPLIT_CONFIG } from '../../shared/videoSplitConfig';
const emit = defineEmits<{
  addedComment: [];
}>();

const { hasChapters } = storeToRefs(useAiMetadataStore());
const { isSameOrgAsItem: canUseLogs } = useUser();

const viewerModeStore = useViewerModeStore();
const { theaterModeStore, isTheaterModeEnabled } = useViewerTheaterMode();
const layoutStore = useLayoutStore();
const { isLogsMode, currentMode } = storeToRefs(viewerModeStore);

const { isMobile } = useViewerAvailableModes();
const { isMobile: isSmallMobile } = useMobileDetection();
const isDesktop = computed(() => !isMobile.value);
useViewerModeUrlSync();

watch(
  isTheaterModeEnabled,
  isEnabled => {
    layoutStore.setTheaterSidebarCollapsed(isEnabled);
  },
  { immediate: true }
);

const splitConfig = computed(() => {
  if (canUseLogs.value && isLogsMode.value) {
    return { defaultRatio: 0.5, minRatio: 0.25, maxRatio: 0.75, storageKey: 'viewer-logs-split-ratio' };
  }
  if (isDesktop.value && hasChapters.value) {
    return VIDEO_RIGHT_PANE_SPLIT_CONFIG;
  }
  return null;
});

const splitRightPanel = computed<Component>(() => {
  if (canUseLogs.value && isLogsMode.value) return LogsSidePanel;
  return GuestChaptersSidePanel;
});

// Defensive guard: if a viewer who can't use Logs somehow lands in LOGS mode
// (e.g. shared URL with ?mode=logs), snap them back to Video.
watchEffect(() => {
  if (!canUseLogs.value && currentMode.value === ViewerMode.LOGS) {
    viewerModeStore.setMode(ViewerMode.VIDEO);
  }
});

onUnmounted(() => {
  layoutStore.setTheaterSidebarCollapsed(false);
  theaterModeStore.disableTheaterMode();
});
</script>

<style scoped>
.video-guest-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-guest-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1440px) {
  .video-guest-layout__media-row {
    grid-template-columns: 1fr 360px;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

.video-guest-layout__video-col {
  @apply flex flex-col min-w-0;
}

.video-guest-layout__video {
  @apply min-w-0;
}

.video-guest-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

@media (min-width: 1024px) {
  .video-guest-layout__video :deep(.viewer-video-theater-frame) {
    height: calc(100vh - 14rem);
    height: calc(100dvh - 14rem);
    max-height: calc(100vh - 14rem);
    max-height: calc(100dvh - 14rem);
    aspect-ratio: auto;
  }

  .video-guest-layout__video :deep(.viewer-video-theater-frame .video-container),
  .video-guest-layout__video :deep(.viewer-video-theater-frame .video-js) {
    height: 100%;
  }

  .video-guest-layout__video :deep(.viewer-video-theater-frame .vjs-tech) {
    object-fit: contain;
  }
}

.video-guest-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

@media (min-width: 1024px) {
  .video-guest-layout__side-panel {
    @apply mt-0;
  }

  .video-guest-layout__side-panel :deep(.guest-chapters-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-guest-layout__tabs {
  @apply mt-2;
}

/* Split view (Logs mode and Video mode with chapters) */
.video-guest-layout__split {
  min-height: 0;
}

.video-guest-layout__split :deep(.video-guest-layout__side-panel) {
  @apply mt-0 h-full;
}

.video-guest-layout__split :deep(.logs-panel) {
  @apply h-full;
}

.video-guest-layout__split :deep(.video-guest-layout__video-col) {
  @apply h-full;
}
</style>
